// Festival map upload / display helpers.
//
// The producer's festival map lives in the private `briefing-maps`
// storage bucket and the briefings row carries a pointer
// (`map_storage_path`). Uploads go through the `upload-briefing-map`
// edge function — that function does the ownership check, MIME / size
// validation, and uses the service-role key to write the bytes. The
// browser never talks to storage directly on the write path.
//
// Read paths must call `resolveFestivalMapUrl` rather than reading
// `map_storage_path` directly so the signed URL plumbing stays in one
// place.

import { supabase } from "@/integrations/supabase/client";
import { validateFestivalMapFile } from "./festivalMapValidation";

const BUCKET = "briefing-maps";
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour

/**
 * Upload a festival map image via the upload-briefing-map edge function.
 *
 * Returns the storage object key (`<briefing_id>/<random>.<ext>`) that
 * should be persisted to briefings.map_storage_path. Callers stay
 * responsible for the briefing UPDATE — this helper only handles the
 * upload step.
 */
export async function uploadFestivalMap(briefingId: string, file: File): Promise<string> {
  // Fast-fail on the same size / type rules the edge function enforces, so a
  // doomed multi-megabyte upload never leaves the browser. The function stays
  // the authoritative guard for callers that skip this helper.
  const validation = validateFestivalMapFile(file);
  if (!validation.ok) throw new Error(validation.message);

  const form = new FormData();
  form.append("briefing_id", briefingId);
  form.append("file", file, file.name);
  const { data, error } = await supabase.functions.invoke<{ path: string }>("upload-briefing-map", {
    body: form,
  });
  if (error) throw error;
  if (!data?.path) throw new Error("upload-briefing-map: no path returned");
  return data.path;
}

/**
 * Resolve a briefing's festival map to a signed URL we can stick in <img src>.
 * Returns null when the briefing has no map.
 */
export async function resolveFestivalMapUrl(briefing: {
  map_storage_path?: string | null;
}): Promise<string | null> {
  if (!briefing.map_storage_path) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(briefing.map_storage_path, SIGNED_URL_TTL_SECONDS);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("resolveFestivalMapUrl: signed URL failed", error);
    return null;
  }
  return data?.signedUrl ?? null;
}
